import { useCssTokens } from '../../hooks/useCssTokens';

const fontFamilies = [
  { label: 'Display', token: 'font-display', sample: 'Space Grotesk' },
  { label: 'Sans', token: 'font-sans', sample: 'Source Sans 3' },
  { label: 'Mono', token: 'font-mono', sample: 'SF Mono' },
];

const typeRoles = [
  {
    id: 'title-lg',
    label: 'Title / Large',
    tokens: {
      size: 'type-title-lg-size',
      lineHeight: 'type-title-lg-line-height',
      weight: 'type-title-lg-weight',
      tracking: 'type-title-lg-tracking',
      font: 'type-title-lg-font',
    },
  },
  {
    id: 'title-md',
    label: 'Title / Medium',
    tokens: {
      size: 'type-title-md-size',
      lineHeight: 'type-title-md-line-height',
      weight: 'type-title-md-weight',
      tracking: 'type-title-md-tracking',
      font: 'type-title-md-font',
    },
  },
  {
    id: 'section',
    label: 'Section Heading',
    tokens: {
      size: 'type-section-size',
      lineHeight: 'type-section-line-height',
      weight: 'type-section-weight',
      tracking: 'type-section-tracking',
      font: 'type-section-font',
    },
  },
  {
    id: 'body',
    label: 'Body',
    tokens: {
      size: 'type-body-size',
      lineHeight: 'type-body-line-height',
      weight: 'type-body-weight',
      tracking: 'type-body-tracking',
      font: 'type-body-font',
    },
  },
  {
    id: 'body-strong',
    label: 'Body Strong',
    tokens: {
      size: 'type-body-strong-size',
      lineHeight: 'type-body-strong-line-height',
      weight: 'type-body-strong-weight',
      tracking: 'type-body-strong-tracking',
      font: 'type-body-strong-font',
    },
  },
  {
    id: 'label',
    label: 'Label',
    tokens: {
      size: 'type-label-size',
      lineHeight: 'type-label-line-height',
      weight: 'type-label-weight',
      tracking: 'type-label-tracking',
      font: 'type-label-font',
    },
  },
  {
    id: 'helper',
    label: 'Helper',
    tokens: {
      size: 'type-helper-size',
      lineHeight: 'type-helper-line-height',
      weight: 'type-helper-weight',
      tracking: 'type-helper-tracking',
      font: 'type-helper-font',
    },
  },
  {
    id: 'caption',
    label: 'Caption',
    tokens: {
      size: 'type-caption-size',
      lineHeight: 'type-caption-line-height',
      weight: 'type-caption-weight',
      tracking: 'type-caption-tracking',
      font: 'type-caption-font',
    },
  },
];

const typeScale = [
  { token: 'font-size-12', label: '12' },
  { token: 'font-size-14', label: '14' },
  { token: 'font-size-16', label: '16' },
  { token: 'font-size-18', label: '18' },
  { token: 'font-size-20', label: '20' },
  { token: 'font-size-24', label: '24' },
  { token: 'font-size-32', label: '32' },
  { token: 'font-size-40', label: '40' },
];

const typographyTokens = [
  ...fontFamilies.map((item) => item.token),
  ...typeScale.map((item) => item.token),
  ...typeRoles.flatMap((role) => Object.values(role.tokens)),
];

export function TypographyShowcase() {
  const values = useCssTokens(typographyTokens);

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Font families</h4>
        <div className="grid grid-cols-3 gap-3">
          {fontFamilies.map((family) => (
            <div key={family.token} className="rounded-2xl border border-border/40 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {family.label}
              </p>
              <p
                className="mt-2 text-lg text-foreground"
                style={{ fontFamily: `var(--${family.token})` }}
              >
                {family.sample}
              </p>
              <p className="mt-2 truncate text-xs text-muted-foreground">{values[family.token]}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Type roles</h4>
        <div className="space-y-4">
          {typeRoles.map((role) => (
            <div key={role.id} className="rounded-2xl border border-border/40 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {role.label}
              </p>
              <p
                className="mt-2 text-foreground"
                style={{
                  fontFamily: `var(--${role.tokens.font})`,
                  fontSize: `var(--${role.tokens.size})`,
                  lineHeight: `var(--${role.tokens.lineHeight})`,
                  fontWeight: `var(--${role.tokens.weight})`,
                  letterSpacing: `var(--${role.tokens.tracking})`,
                }}
              >
                The quick brown fox jumps over the lazy dog.
              </p>
              <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                <div>size: {values[role.tokens.size]}</div>
                <div>line-height: {values[role.tokens.lineHeight]}</div>
                <div>weight: {values[role.tokens.weight]}</div>
                <div>tracking: {values[role.tokens.tracking]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Type scale</h4>
        <div className="grid grid-cols-4 gap-3 lg:grid-cols-8">
          {typeScale.map((scale) => (
            <div key={scale.token} className="rounded-2xl border border-border/40 p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {scale.label}
              </p>
              <p className="mt-1 text-foreground" style={{ fontSize: `var(--${scale.token})` }}>
                Aa
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{values[scale.token]}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
